import React, { useEffect, useMemo } from "react";
import { useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Table,
  Image,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import moment from "moment";
import Images from "../../CRMs/crm2.0";
import {
  convertValue,
  formatCalculatedValue,
  formatValue,
  getMonthlyPayment,
  getMraAge,
  integerValue,
  showValue,
} from "../../../services/helperService";
import { usePlannerContext } from "../../../contexts/planner.contexts";

const ConclusionForm = ({ submit, validated }) => {
  const { plannerData, handleBlur, onChangeHandler, handleFocus, srsEligible } =
    usePlannerContext();

  const [formData, setformData] = useState({});

  const [toolTipsSH, setToolTipsSH] = useState([false, false]);

  const [fersBasicAnnuity, setFersBasicAnnuity] = useState();

  const [savings, setSavings] = useState("0.00");

  const [calculatedData, setCalculatedData] = useState({});

  const checkSSEligibility = () => {
    return plannerData["s8s2_age_to_begin"] <= plannerData["MRA"];
  };

  const isEligibleFor55 = (dob) => {
    if (!dob || !dob.isValid()) return false; // ID: 27

    const currentYear = moment().year();
    const birthYear = dob.year();
    const age = currentYear - birthYear;

    const hasHadBirthday =
      dob.month() < moment().month() ||
      (dob.month() === moment().month() && dob.date() <= moment().date());

    return age >= 55 || (age === 54 && hasHadBirthday);
  };

  const toolTipShowHide = (show, index) => {
    let tp = [...toolTipsSH];
    tp[index] = show;
    setToolTipsSH(tp);
  };

  const InfoTooltip = ({ text, show }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <span
        style={{
          cursor: "pointer",
          marginLeft: "0px",
          visibility: show ? "visible" : "hidden",
        }}
      >
        <i className="fa fa-info-circle"></i>
      </span>
    </OverlayTrigger>
  );

  useEffect(() => {
    const fersAnnuityArr = [
      plannerData["fersBasicAnnuity"],
      plannerData["fersBasicAnnuity95"],
      plannerData["fersBasicAnnuity90"],
    ];
    const valueToUse = plannerData["valueToUse"]
      ? plannerData["valueToUse"] - 1
      : 0;
    const fersBasicAnnuity = convertValue(fersAnnuityArr[valueToUse]);
    const annualPay = convertValue(plannerData["pay_s2_1"]);
    const s8s8Income = checkSSEligibility()
      ? convertValue(plannerData["s8s2_income_amount"])
      : 0;

    let srsyears = plannerData["yearsOfService"];
    if (
      plannerData.have_military_service == "yes" &&
      plannerData.s2s3_receiving_pension == "no"
    ) {
      let month = convertValue(plannerData.military_service_years) * 12;
      month = month + convertValue(plannerData.military_service_months);
      let year = month / 12;
      srsyears = srsyears - year;
    }

    const Srs =
      convertValue(plannerData["age_62_ss_benefit"]) *
      (parseInt(srsyears) / 40);

    const age_62_ss_benefit_final = convertValue(Srs);
    let tsp_s2_balance = convertValue(plannerData["tsp_s2_balance"]);
    const today = annualPay / 12;

    tsp_s2_balance =
      !isEligibleFor55(plannerData["dob"]) && plannerData["ver_eligible"] == "1"
        ? 0
        : tsp_s2_balance * 0.0045;
    const reterement = srsEligible(plannerData)
      ? fersBasicAnnuity + Srs + tsp_s2_balance
      : fersBasicAnnuity + s8s8Income + tsp_s2_balance;
    const gap = today - convertValue(reterement);
    const youCurrentlyHave = getTotalOfIncomeSources();

    const diffVal = today - youCurrentlyHave;

    const diffInGap = diffVal > 0 ? diffVal : 0;

    const gapPerc = parseInt((youCurrentlyHave * 100) / today);
    setCalculatedData({
      today: formatValue(today),
      reterement: formatValue(reterement),
      gap: formatValue(gap),
      fersBasicAnnuity: formatValue(fersBasicAnnuity),
      s8s8Income: formatValue(s8s8Income),
      tsp_s2_balance: formatValue(tsp_s2_balance),
      totalAmount: formatValue(fersBasicAnnuity + s8s8Income + tsp_s2_balance),
      age_62_ss_benefit_final: formatValue(age_62_ss_benefit_final),
      youCurrentlyHave: formatValue(youCurrentlyHave),
      diffInGap: "$" + formatValue(diffInGap),
      diffVal: diffVal,
      gapPerc: gapPerc,
    });
  }, [plannerData]);

  /**
   * Hook to calculate retirement timeline splits
   * Mirrors PHP logic for building $splits array
   */

  const buildIncomeTimeline = () => {
    const splits = retirementSplits();

    return splits.map((age, index) => {
      const nextAge = splits[index + 1] - 1;

      let incomes = [];

      // -----------------------------
      // FERS Annuity (always present)
      // -----------------------------

      const fersAnnuityArr = [
        plannerData["fersBasicAnnuity"],
        plannerData["fersBasicAnnuity95"],
        plannerData["fersBasicAnnuity90"],
      ];
      const valueToUse = plannerData["valueToUse"]
        ? plannerData["valueToUse"] - 1
        : 0;
      const fersBasicAnnuity = convertValue(fersAnnuityArr[valueToUse]);

      if (fersBasicAnnuity) {
        incomes.push({
          label: "FERS Annuity",
          gross: Number(convertValue(fersBasicAnnuity)),
          net: Number(convertValue(fersBasicAnnuity)), // adjust if deductions exist
          note: getFersNote(),
        });
      }

      // -----------------------------
      // SRS
      // -----------------------------
      if (age < 62 && srsEligible(plannerData)) {
        let srsyears = plannerData["yearsOfService"];
        if (
          plannerData.have_military_service == "yes" &&
          plannerData.s2s3_receiving_pension == "no"
        ) {
          let month = convertValue(plannerData.military_service_years) * 12;
          month = month + convertValue(plannerData.military_service_months);
          let year = month / 12;
          srsyears = srsyears - year;
        }

        const estimatedsrs =
          convertValue(plannerData["age_62_ss_benefit"]) *
          (parseInt(srsyears) / 40);

        const val = Number(estimatedsrs || 0);
        incomes.push({
          label: "SRS",
          gross: val,
          net: val,
        });
      }

      // -----------------------------
      // Social Security
      // -----------------------------
      if (
        age >= plannerData.s8s2_age_to_begin &&
        plannerData?.s8s2_currently_receiving_social_security === "no" &&
        plannerData?.s8_will_receive_social_sec_payment === "yes"
      ) {
        const val = Number(convertValue(plannerData.s8s2_income_amount) || 0);
        incomes.push({
          label: "Social Security",
          gross: val,
          net: val,
        });
      }

      // -----------------------------
      // Military Pension
      // -----------------------------
      if (
        plannerData.s2s3_receiving_pension === "yes" &&
        plannerData.s10_monthly_military_pension
      ) {
        const val = Number(plannerData.s10_monthly_military_pension);
        incomes.push({
          label: "Military Pension",
          gross: val,
          net: val,
        });
      }

      if (plannerData.have_other_pension === "yes") {
        const val = Number(plannerData.have_other_pension_monthly_amount);
        const lab = plannerData.have_other_pension_name;
        incomes.push({
          label: lab,
          gross: val,
          net: val,
        });
      }

      if (
        plannerData.additional_sources_of_income_at_retire?.includes(
          "Other income"
        )
      ) {
        const val = Number(
          plannerData.additional_sources_of_income_at_retire_other_income_amount
        );
        const lab =
          plannerData.additional_sources_of_income_at_retire_other_income_name;
        incomes.push({
          label: lab,
          gross: val,
          net: val,
        });
      }

      // -----------------------------
      // Disability
      // -----------------------------
      if (
        plannerData.s2s3_receiving_disability_pension === "yes" &&
        plannerData.s10_monthly_disability_amount
      ) {
        const val = Number(plannerData.s10_monthly_disability_amount);
        incomes.push({
          label: "Military Disability",
          gross: val,
          net: val,
        });
      }

      // -----------------------------
      // TOTALS
      // -----------------------------
      const grossTotal = incomes.reduce((sum, i) => sum + i.gross, 0);
      const netTotal = incomes.reduce((sum, i) => sum + i.net, 0);

      return {
        ageStart: age,
        ageEnd: nextAge || null,
        incomes,
        grossTotal,
        netTotal,
      };
    });
  };

  const generateIncomeSources = () => {
    const timelineData = buildIncomeTimeline();
    let incomes = [];
    timelineData.forEach((item) => {
      item.incomes.forEach((income) => {
        incomes.push(income);
      });
    });

    const unique = Array.from(
      new Set(incomes.map((item) => JSON.stringify(item)))
    ).map((item) => JSON.parse(item));

    const rows = [];

    for (let i = 0; i < unique.length; i += 2) {
      rows.push(
        <tr key={i}>
          <th style={{ padding: "10px 20px", fontSize: "18px" }}>
            {unique[i]?.label}
          </th>
          <th style={{ padding: "10px 20px", fontSize: "18px" }}>
            {showValue(unique[i]?.gross)}
          </th>
          <th style={{ padding: "10px 20px", fontSize: "18px" }}>
            {unique[i + 1]?.label || ""}
          </th>
          <th style={{ padding: "10px 20px", fontSize: "18px" }}>
            {unique[i + 1] ? showValue(unique[i + 1].gross) : ""}
          </th>
        </tr>
      );
    }

    return rows;
  };

  const getTotalOfIncomeSources = () => {
    try {
      const timelineData = buildIncomeTimeline();
      let incomes = [];

      if (timelineData !== null && timelineData !== undefined) {
        timelineData.forEach((item) => {
          if (item !== null && item !== undefined) {
            item.incomes.forEach((income) => {
              if (income !== null && income !== undefined) {
                incomes.push(income);
              }
            });
          }
        });

        const unique = Array.from(
          new Set(incomes.map((item) => JSON.stringify(item)))
        ).map((item) => JSON.parse(item));

        return unique.reduce((sum, i) => {
          if (
            i !== null &&
            i !== undefined &&
            i.gross !== null &&
            i.gross !== undefined
          ) {
            return sum + i.gross;
          }
          return sum;
        }, 0);
      }
    } catch (error) {
      console.error("Error in getTotalOfIncomeSources: ", error);
    }

    return 0;
  };

  const generateIncomesTimeline = () => {
    const timelineData = buildIncomeTimeline();

    return timelineData.map((item, index) => (
      <>
        <div className="p-text-wrapper mb-2">
          <p className="main-p">
            <strong>
              Age {item.ageStart}
              {item.ageEnd ? `-${item.ageEnd}` : "+"}:
            </strong>
          </p>
        </div>

        <div className="text-space">
          {item.incomes.map((income, i) => (
            <Row key={i}>
              <Col md={1}></Col>
              <Col md={3}>{income.label}:</Col>
              <Col md={6}>
                {showValue(income.gross)}
                {income.note && (
                  <span style={{ fontSize: "12px" }}> {income.note}</span>
                )}
              </Col>
            </Row>
          ))}

          {/* GROSS */}
          <Row style={{ fontWeight: "bold", fontSize: "20px" }}>
            <Col md={1}></Col>
            <Col
              md={3}
              style={{ textAlign: "right", borderTop: "2px solid #000" }}
            >
              GROSS:
            </Col>
            <Col md={3} style={{ borderTop: "2px solid #000" }}>
              {showValue(item.grossTotal)}
            </Col>
          </Row>

          {/* NET */}
          {/* <Row
            style={{
              fontWeight: "bold",
              color: "#00b050",
              fontSize: "20px",
            }}
          >
            <Col md={1}></Col>
            <Col
              md={3}
              style={{ textAlign: "right", borderTop: "2px solid #000" }}
            >
              NET:
            </Col>
            <Col md={3} style={{ borderTop: "2px solid #000" }}>
              {showValue(item.netTotal)}
            </Col>
          </Row> */}
        </div>
      </>
    ));
  };

  const getFersNote = () => {
    if (
      plannerData.s9_check_to_bb_military_time === "1" &&
      plannerData.pre1989_part_time_service === "yes"
    ) {
      return "(Presumes completed military & pre-1989 time buy back)";
    }

    if (plannerData.pre1989_part_time_service === "yes") {
      return "(Presumes completed pre-1989 time buy back)";
    }

    if (plannerData.s9_check_to_bb_military_time === "1") {
      return "(Presumes completed military time buy back)";
    }

    return "";
  };

  const retirementSplits = () => {
    const retirementAge = plannerData.MRA;
    if (!plannerData) return [];

    let splits = [];

    const ageToBegin = plannerData?.s8s2_age_to_begin;
    // const tspNoOfMonths = plannerData?.finalReportField3;

    // Determine social security presence
    const socialSecurityPresent =
      plannerData?.s8s2_currently_receiving_social_security === "no" &&
      plannerData?.s8_will_receive_social_sec_payment === "yes";

    // -----------------------------
    // COMID : 3031
    // -----------------------------
    splits.push(parseInt(retirementAge));

    // -----------------------------
    // COMID : 3032
    // -----------------------------
    if (retirementAge < 62) {
      splits.push(62);
    }

    // -----------------------------
    // Social Security Branch Logic
    // -----------------------------
    if (socialSecurityPresent && ageToBegin >= retirementAge) {
      // COMID : 3038
      if (!splits.includes(parseInt(ageToBegin))) {
        splits.push(parseInt(ageToBegin));
      }
    }

    // -----------------------------
    // Final processing (sort + unique)
    // -----------------------------
    splits = splits.filter((v) => !isNaN(v)).map(Number);

    splits = [...new Set(splits)].sort((a, b) => a - b);

    // if (splits[splits.length - 1] < 70) {
    //   splits.push(70);
    // }

    return splits;
  };

  useEffect(() => {
    if (
      plannerData &&
      typeof plannerData === "object" &&
      Object.keys(plannerData).length > 0
    ) {
      const FEHB =
        convertValue(plannerData["pay_s2_32"]) +
        "." +
        convertValue(plannerData["pay_s2_33"]);
      const tsp_rate =
        convertValue(plannerData["pay_tsp_r_1"]) +
        "." +
        convertValue(plannerData["pay_tsp_r_2"]);
      const stateTax =
        convertValue(plannerData["pay_s2_8"]) +
        "." +
        convertValue(plannerData["pay_s2_9"]);
      const fefTax =
        convertValue(plannerData["pay_s2_5"]) +
        "." +
        convertValue(plannerData["pay_s2_6"]);
      const TAX = convertValue(fefTax) + convertValue(stateTax);

      const BASIC_LIFE = convertValue(plannerData["fegli_line4"]) * 0.25;
      const OPTION_A = convertValue(plannerData["option_a"]) * 0.25;
      const PROTECTION_TOTAl = OPTION_A + BASIC_LIFE;
      const monthlyGross = convertValue(plannerData["pay_s2_1"]) / 12;

      const fersAnnuityArr = [
        plannerData["fersBasicAnnuity"],
        plannerData["fersBasicAnnuity95"],
        plannerData["fersBasicAnnuity90"],
      ];
      const valueToUse = plannerData["valueToUse"]
        ? plannerData["valueToUse"] - 1
        : 0;
      setFersBasicAnnuity(fersAnnuityArr[valueToUse]);
      const Loss = convertValue(plannerData["loss_in_monthly_income"]);
      let srsyears = plannerData["yearsOfService"];
      if (
        plannerData.have_military_service == "yes" &&
        plannerData.s2s3_receiving_pension == "no"
      ) {
        let month = convertValue(plannerData.military_service_years) * 12;
        month = month + convertValue(plannerData.military_service_months);
        let year = month / 12;
        srsyears = srsyears - year;
      }

      const estimatedsrs =
        convertValue(plannerData["age_62_ss_benefit"]) *
        (parseInt(srsyears) / 40);
      const SURVIVOR = convertValue(fersBasicAnnuity) * 0.1;
      const TAXDEDUCTION = convertValue(fersBasicAnnuity) * 0.2;

      const FEHBINSURANCE =
        convertValue(plannerData["pay_s2_32"]) +
        "." +
        convertValue(plannerData["pay_s2_33"]);
      const TRADITIONALINSURANCE =
        convertValue(plannerData["pay_s2_26"]) +
        "." +
        convertValue(plannerData["pay_s2_27"]);
      const ROTHINSRANCE =
        convertValue(plannerData["pay_tsp_r_1"]) +
        "." +
        convertValue(plannerData["pay_tsp_r_2"]);
      const serviceYears =
        plannerData["special_provisions"] == "0" &&
        plannerData["ver_eligible"] == "0"
          ? plannerData["yearsOfService"]
          : convertValue(plannerData["yearsOfService1"]) +
            convertValue(plannerData["yearsOfService2"]);

      let totalMonthIncomeRIP = 0;
      totalMonthIncomeRIP = convertValue(plannerData["fersBasicAnnuity"]);
      if (plannerData["item_srs"] && plannerData["age"] < 62) {
        totalMonthIncomeRIP += convertValue(plannerData["item_srs"]);
      } else {
        totalMonthIncomeRIP += convertValue(plannerData["age_62_ss_benefit"]);
      }

      let savingAmount = plannerData["expenses_field_monthly_total"];
      savingAmount =
        convertValue(savingAmount) * (plannerData["age"] < 65 ? 6 : 12);
      savingAmount = formatCalculatedValue(savingAmount);
      setSavings(savingAmount);

      let TspStatus = "";
      const tspPayload = getMonthlyPayment(
        plannerData["tsp_opt_amount"],
        plannerData["MRA"]
      );
      let tspAmount = 0;
      if (plannerData["lifeAnnuity"] == "tsp_opt_full_or_partial_wid") {
        TspStatus = "Full or Partial Withdrawal";
        tspAmount = 0;
      } else if (plannerData["lifeAnnuity"] == "tsp_opt_tsp_monthly_pay") {
        TspStatus = "TSP Monthly Payments";
        tspAmount = 0;
      } else if (
        plannerData["lifeAnnuity"] == "tsp_opt_single_life_annuity_tsp"
      ) {
        TspStatus = "Single Life Annuity TSP";
        tspAmount = convertValue(tspPayload["SLA TSP"]);
      } else if (
        plannerData["lifeAnnuity"] ==
        "tsp_opt_single_life_annuity_private_sector"
      ) {
        tspAmount = convertValue(tspPayload["SLA PS"]);
        TspStatus = "Single Life Annuity Private Sector";
      } else if (
        plannerData["lifeAnnuity"] == "tsp_opt_joint_life_annuity_tsp"
      ) {
        TspStatus = "Joint Life Annuity TSP";
        tspAmount = convertValue(tspPayload["JLA TSP"]);
      } else if (
        plannerData["lifeAnnuity"] ==
        "tsp_opt_joint_life_annuity_private_sector"
      ) {
        TspStatus = "Joint Life Annuity Private Sector";
        tspAmount = convertValue(tspPayload["JLA PS"]);
      }
      totalMonthIncomeRIP += tspAmount;

      if (plannerData["pdf"]) {
        totalMonthIncomeRIP += convertValue(
          plannerData["conclusion_ret_inc_pro_other"]
        );
      }

      const totalInstitutionalBalance =
        plannerData["s14_balance"] &&
        Array.isArray(plannerData["s14_balance"]) &&
        plannerData["s14_balance"].reduce((amount, amount2) => {
          return parseFloat(amount) + parseFloat(amount2);
        }, 0);
      const emergencyCashNeeded =
        convertValue(savingAmount) - totalInstitutionalBalance;
      const remainYears = moment(plannerData["desired_retirement_date"]).diff(
        moment(),
        "year"
      );
      const total =
        formData.yearsOfServiceat62 && formData.age_62_ss_benefit
          ? (convertValue(formData.yearsOfServiceat62) *
              convertValue(formData.age_62_ss_benefit)) /
            40
          : "0.00";
      let payload = {};
      setformData({
        ...plannerData,
        age_62_ss_benefit_final: formatCalculatedValue(total),
        FEHB: formatCalculatedValue(FEHB),
        tsp_rate: formatCalculatedValue(tsp_rate),
        TAX: formatCalculatedValue(TAX),
        BASIC_LIFE: formatCalculatedValue(BASIC_LIFE),
        PROTECTION_TOTAl: formatCalculatedValue(PROTECTION_TOTAl),
        Loss: formatCalculatedValue(Loss),
        remainYears: formatCalculatedValue(remainYears),
        monthlyGross: formatCalculatedValue(monthlyGross),
        estimatedsrs: formatCalculatedValue(estimatedsrs),
        TAXDEDUCTION: formatCalculatedValue(TAXDEDUCTION),
        SURVIVOR: formatCalculatedValue(SURVIVOR),
        FEHBINSURANCE: formatCalculatedValue(FEHBINSURANCE),
        ROTHINSRANCE: formatCalculatedValue(ROTHINSRANCE),
        TRADITIONALINSURANCE: formatCalculatedValue(TRADITIONALINSURANCE),
        serviceYears: serviceYears,
        emergencyCashNeeded: formatCalculatedValue(emergencyCashNeeded),
        totalInstitutionalBalance: formatCalculatedValue(
          totalInstitutionalBalance
        ),
        tspAmount: formatCalculatedValue(tspAmount),
        TspStatus,
        totalMonthIncomeRIP: formatCalculatedValue(totalMonthIncomeRIP),
        OPTION_A: formatCalculatedValue(OPTION_A),
        yearsOfServiceat62: formatCalculatedValue(srsyears),
        ...payload,
      });
    }
  }, [plannerData]);

  const submitFormdata = (event) => {
    submit(event, formData);
  };
  return (
    <>
      <Card.Body className="conclusion">
        <Form
          name="conclusionForm"
          onSubmit={submitFormdata}
          className="employee-form"
          noValidate
          validated={validated}
        >
          <div className="text-center">
            <div className="head-title">CONCLUSION</div>
          </div>

          <section>
            <div className="box-wrapper">
              <div className="box-body">
                <p className="heading"></p>

                <div className="body-text-wrapper">
                  <div className="text-space ">
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4} className="text-bold">
                        Name:
                      </Col>
                      <Col md={4}>
                        {formData["firstname"]} {formData["lastname"]}
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4} className="text-bold">
                        Date:
                      </Col>
                      <Col md={4}>{moment().format("MM/DD/YYYY")}</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4} className="text-bold text-nowrap">
                        Retirement Eligibility:
                      </Col>
                      <Col md={4}>
                        Age {integerValue(formData.MRA)} w/{" "}
                        {showValue(formData.yearsOfService, false)} years of
                        service
                      </Col>
                    </Row>
                    {formData["do_you_have_children"] == "yes" && (
                      <Row>
                        <Col md={1}></Col>
                        <Col md={4} className="text-bold">
                          Dependent Children:
                        </Col>
                        <Col md={4} className="text-capitalize">
                          {formData["dependent_children"]}
                        </Col>
                      </Row>
                    )}
                  </div>
                </div>
                <div className="body-text-wrapper mt-3 ">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>PAY</strong> <span className="sub-p">(USPS)</span>
                    </p>
                  </div>

                  <Row className="mt-4">
                    <Col md={1}></Col>
                    <Col md={4}>Base Rate of Pay</Col>
                    <Col md={4}>{showValue(formData["pay_s2_1"])}</Col>
                  </Row>
                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>Net Pay</Col>
                    <Col md={4}>{showValue(formData["pay_s2_36"])}</Col>
                  </Row>
                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>Bi-weekly Pay Gross Pay</Col>
                    <Col md={4}>{showValue(formData["pay_s2_2"])}</Col>
                  </Row>
                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>FEHB</Col>
                    <Col md={4}> {showValue(formData["FEHB"])}</Col>
                  </Row>
                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>FEGLI</Col>
                    <Col md={4}> {showValue(formData["fegli_line4"])}</Col>
                  </Row>
                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>
                      TSP
                      {formData["pay_s2_25"] == "$"
                        ? "(Fixed)"
                        : formData["pay_s2_25"] == "%"
                        ? "(" + formData["pay_s2_tsp_percent"] + "%)"
                        : "()"}
                    </Col>
                    <Col md={4}>
                      {" "}
                      {formData["pay_s2_26"]
                        ? showValue(formData["pay_s2_26"])
                        : "$0"}
                    </Col>
                  </Row>
                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>
                      TSP R
                      {formData["pay_s2_100"] == "$"
                        ? "(Fixed)"
                        : formData["pay_s2_100"] == "%"
                        ? "(" + formData["pay_s2_tsp_r_percent"] + "%)"
                        : "()"}
                    </Col>
                    <Col md={4}>
                      {" "}
                      {formData["tsp_rate"]
                        ? showValue(formData["tsp_rate"])
                        : "$0"}
                    </Col>
                  </Row>
                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>Fed and State Withholdings</Col>
                    <Col md={4}> {showValue(formData["TAX"])}</Col>
                  </Row>
                </div>
              </div>
            </div>
          </section>
          <section className="mt-3">
            <div className="box-wrapper">
              <div className="box-body">
                <p className="heading">
                  FEGLI - Federal Employee Group Life Insurance
                </p>

                <div className="body-text-wrapper">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>Short-Term Protection </strong>
                      <span className="sub-p-normal">(Today to Age 60)</span>
                    </p>
                  </div>
                  <div className="text-space">
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Basic Life</Col>
                      <Col md={3}>{showValue(formData["fegli_line4"])}</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Extra Benefit</Col>
                      <Col md={2}> {showValue(formData["fegli_line7"])}</Col>
                      <Col md={4}>
                        <div className="error-text">* Eliminated at Age 45</div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Option A</Col>
                      <Col md={2}>{showValue(formData["option_a"])}</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Option B</Col>
                      <Col md={2}> {showValue(formData["fegli_line10"])}</Col>
                      <Col md={4}>
                        <div className="error-text">** Increase in premium</div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Option C</Col>
                      <Col md={2}></Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4} className="col-spacing">
                        Spouse
                      </Col>
                      <Col md={2}> {showValue(formData["optionCSpouce"])}</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4} className="table-border col-spacing">
                        Child (Under age 22 or disabled)
                      </Col>
                      <Col md={2} className="table-border">
                        {showValue(formData["optionCChildren"])}
                      </Col>
                    </Row>

                    <Row className="">
                      <Col md={1}></Col>
                      <Col md={4} className="total-text">
                        Total
                      </Col>
                      <Col md={2} className="total-text">
                        {showValue(formData["total_life_insurance"])}
                      </Col>
                    </Row>
                  </div>
                </div>
                <div className="body-text-wrapper mt-3">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>Permanent Protection </strong>
                      <span className="sub-p-normal">(Age 65+)</span>
                    </p>
                  </div>
                  <div className="text-space ">
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Basic Life</Col>
                      <Col md={2}>{showValue(formData["BASIC_LIFE"])}</Col>
                      <Col md={4}>
                        <div className="error-text">
                          * 75% reduction at Age 65
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Extra Benefit</Col>
                      <Col md={2}>N/A</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Option A</Col>
                      <Col md={2}> {showValue(formData["OPTION_A"])}</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Option B</Col>
                      <Col md={2}>N/A</Col>
                      <Col md={4}>
                        <div className="error-text">
                          * Unaffordable @ {showValue(formData["age65OptionB"])}{" "}
                          mo.
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Option C</Col>
                      <Col md={3}></Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4} className="col-spacing">
                        Spouse
                      </Col>
                      <Col md={2}>N/A</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4} className="table-border col-spacing">
                        Child (Under age 22 or disabled)
                      </Col>
                      <Col md={2} className="table-border">
                        N/A
                      </Col>
                    </Row>

                    <Row className="">
                      <Col md={1}></Col>
                      <Col md={4} className="total-text">
                        Total
                      </Col>
                      <Col md={2} className="total-text">
                        {showValue(formData["PROTECTION_TOTAl"])}
                      </Col>
                    </Row>
                  </div>
                </div>
                <div className="body-text-wrapper mt-3">
                  <div className="heading-gray-wrapper">
                    <p className="heading-gray">FEGLI - ITEMS OF IMPORTANCE</p>
                  </div>
                  <ul className="heading-gray-ul">
                    <li>
                      * <strong>Extra Benefit</strong> eliminated for all
                      Federal Employees at 45.
                    </li>
                    <li>
                      ** <strong>Option B</strong> Coverage increases
                      automatically every 5 yrs.
                    </li>
                    <li>
                      ** Personal health and cost to fix FEGLI will be
                      jeopardized as you get older.
                    </li>
                    <li>** Permanent Protection not sufficient at Age 90</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          <section className="mt-3">
            <div className="box-wrapper">
              <div className="box-body">
                <p className="heading">Retirement Planning</p>
                <div className="body-text-wrapper mt-3">
                  <div className="section" style={{ marginBottom: "0" }}>
                    <Row>
                      <Col sm={12} md={4}>
                        <Card
                          className="mb-3"
                          style={{ border: "1px solid rgb(127, 191, 127)" }}
                          onMouseEnter={(e) => toolTipShowHide(true, 0)}
                          onMouseLeave={(e) => toolTipShowHide(false, 0)}
                        >
                          <Card.Header
                            style={{
                              fontSize: "20px",
                              fontWeight: "bold",
                              textAlign: "center",
                              backgroundColor: "#7FBF7F",
                              borderColor: "#7FBF7F",
                              color: "#fff",
                            }}
                          >
                            You Currently Have{" "}
                            <InfoTooltip
                              show={toolTipsSH[0]}
                              text="This is the total of Your Income Sources shown below."
                            />
                          </Card.Header>
                          <Card.Body
                            style={{
                              fontSize: "20px",
                              textAlign: "center",
                              margin: 0,
                            }}
                          >
                            <strong>
                              {showValue(calculatedData["youCurrentlyHave"])}
                              /m
                            </strong>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col sm={12} md={4}>
                        <Card
                          className="mb-3"
                          style={{ border: "1px solid rgb(127, 191, 127)" }}
                          onMouseEnter={(e) => toolTipShowHide(true, 1)}
                          onMouseLeave={(e) => toolTipShowHide(false, 1)}
                        >
                          <Card.Header
                            style={{
                              fontSize: "20px",
                              fontWeight: "bold",
                              textAlign: "center",
                              backgroundColor: "#7FBF7F",
                              borderColor: "#7FBF7F",
                              color: "#fff",
                            }}
                          >
                            You Will Need{" "}
                            <InfoTooltip
                              show={toolTipsSH[1]}
                              text="This is your current pre-retirement monthly pay."
                            />
                          </Card.Header>
                          <Card.Body
                            style={{
                              fontSize: "20px",
                              textAlign: "center",
                              margin: 0,
                            }}
                          >
                            <strong>
                              {showValue(calculatedData["today"])}
                              /m
                            </strong>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col sm={12} md={4}>
                        <Card
                          className="mb-3"
                          style={{
                            borderColor:
                              calculatedData["diffVal"] > 0
                                ? "#F5453D"
                                : "#7FBF7F",
                          }}
                        >
                          <Card.Header
                            style={{
                              fontSize: "20px",
                              fontWeight: "bold",
                              textAlign: "center",
                              backgroundColor:
                                calculatedData["diffVal"] > 0
                                  ? "#F5453D"
                                  : "#7FBF7F",
                              borderColor:
                                calculatedData["diffVal"] > 0
                                  ? "#F5453D"
                                  : "#7FBF7F",
                              color: "#fff",
                            }}
                          >
                            Income Gap
                          </Card.Header>
                          <Card.Body
                            style={{
                              fontSize: "20px",
                              textAlign: "center",
                              margin: 0,
                            }}
                          >
                            <strong>{calculatedData["diffInGap"]} /m</strong>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>

                  <div className="section">
                    <div className="container">
                      <Row>
                        <Col style={{ marginLeft: 0, padding: 0 }} md={2}>
                          <div
                            className="alert alert-success mx-auto"
                            role="alert"
                            style={{
                              borderRadius: "50%",
                              width: "100px",
                              height: "100px",
                              textAlign: "center",
                              backgroundColor:
                                calculatedData["gapPerc"] > 84
                                  ? "#7FBF7F"
                                  : "#F5453D",
                              borderColor:
                                calculatedData["gapPerc"] > 84
                                  ? "#7FBF7F"
                                  : "#F5453D",
                              color: "#fff",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "25px",
                                fontWeight: "700",
                                lineHeight: "70px",
                              }}
                            >
                              {calculatedData["gapPerc"]}
                              <sup>%</sup>
                            </span>
                          </div>
                        </Col>
                        <Col md={10} style={{ marginLeft: 0, padding: 0 }}>
                          <Card>
                            <Card.Header
                              style={{ fontSize: "20px", textAlign: "center" }}
                            >
                              Your Income Sources
                            </Card.Header>
                            <Card.Body className="p-0" style={{ margin: 0 }}>
                              <Row>
                                <Table
                                  className="table table-striped table-bordered"
                                  style={{ width: "100%", margin: 0 }}
                                >
                                  {generateIncomeSources()}
                                </Table>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>

                <div className="body-text-wrapper mt-3">
                  <div className="heading-gray-wrapper">
                    <p className="heading-gray">
                      Retirement Planning - ITEMS OF IMPORTANCE
                    </p>
                  </div>
                  <ul className="heading-gray-ul">
                    {formData["s2s3_receiving_pension"] != "yes" &&
                      formData["deposit_made"] != "yes" && (
                        <li>
                          *Verify<strong> Military Time</strong> is credited.
                        </li>
                      )}
                    {formData["family_status"] != "single" && (
                      <li>
                        **If Married and/or Children. Verify{" "}
                        <strong>beneficiary designations</strong> are correct
                        and survivorship options are discussed. Including TSP
                        Annuity..
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-3">
            <div className="box-wrapper">
              <div className="box-body">
                <p className="heading">
                  FERS - Federal Employee Retirement System
                </p>

                <div className="body-text-wrapper">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>FERS </strong>{" "}
                      <span className="sub-p">FERS Annuity Estimate</span>
                    </p>
                  </div>
                  <div>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={5}>Current Age</Col>
                      <Col md={3}>{integerValue(formData["age"])}</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={5} className="text-nowrap text-bold">
                        Desired Retirement Date
                      </Col>
                      <Col md={3}>
                        {moment(formData["desired_retirement_date"]) &&
                          moment(formData["desired_retirement_date"]).format(
                            "MM/DD/yyyy"
                          ) + ` Age ${integerValue(formData["MRA"])}`}
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={5}>Years of Service</Col>
                      <Col md={3}>
                        {" "}
                        {formatValue(formData["yearsOfService"])} years of
                        service
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={5} className="text-nowrap">
                        High Three Salary (pre-retirement)
                      </Col>
                      <Col md={3}>${formData["high3Avg"]}</Col>
                    </Row>
                  </div>
                </div>
                <div className="body-text-wrapper mt-3">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>SRS</strong>
                      <span className="sub-p">
                        {" "}
                        Special Retirement Supplement (to Age 62)
                      </span>
                    </p>
                  </div>
                  {srsEligible(formData) ? (
                    <div className="text-space">
                      <Row>
                        <Col md={1}></Col>
                        <Col md={5} className="text-nowrap">
                          Estimated Age 62 Social Security:
                        </Col>
                        <Col md={3}>${formData["age_62_ss_benefit"]}</Col>
                      </Row>
                      <Row>
                        <Col md={1}></Col>
                        <Col md={5}>Years of Service @ MRA:</Col>
                        <Col md={3}>
                          {" "}
                          {parseInt(formData["yearsOfService"])} years
                        </Col>
                      </Row>
                      <Row>
                        <Col md={1}></Col>
                        <Col md={5} className="conclusion-green text-bold">
                          Estimated SRS Income (until 62)
                        </Col>
                        <Col md={3} className="conclusion-green text-bold">
                          {showValue(formData["estimatedsrs"])}{" "}
                          <span className="conclusion-span">
                            (until Age 62)
                          </span>
                        </Col>
                        <Col md={3} className="error-text text-bold">
                          ** (Taxable Income)
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <div className="text-space">
                      <Row>
                        <Col md={1}></Col>
                        <Col md={5} className="text-nowrap">
                          {formData["MRA"] < getMraAge(plannerData.dob) ? (
                            <b>SRS will start the month after you reach MRA.</b>
                          ) : (
                            "Will Not Qualify for SRS"
                          )}
                        </Col>
                        <Col md={3}></Col>
                      </Row>
                    </div>
                  )}
                </div>
                <div className="body-text-wrapper mt-3">
                  <div className="heading-gray-wrapper">
                    <p className="heading-gray">
                      FERS SRS - ITEMS OF IMPORTANCE
                    </p>
                  </div>
                  <ul className="heading-gray-ul">
                    <li>
                      *Loss of Income Per Month after Retirement (
                      {showValue(formData["Loss"])})
                    </li>
                    <li>
                      *Survivor Deduction ({showValue(formData["SURVIVOR"])})
                    </li>
                    <li>
                      **Estimated 20% Fed and State Tax Deduction (
                      {showValue(formData["TAXDEDUCTION"])})
                    </li>
                    <li>**FERS and SRS payments are fully taxable</li>
                    <li>****PSHB Health Insurance Premiums (N/A)</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-3">
            <div className="box-wrapper ">
              <div className="box-body">
                <p className="heading">TSP - Thrift Savings Plan</p>
                {(formData["pay_s2_26"] || formData["pay_tsp_r_1"]) && (
                  <>
                    {(formData["pay_s2_25"] == "%" ||
                      formData["pay_s2_25"] == "$") && (
                      <>
                        <div className="body-text-wrapper  mt-3">
                          <div className="p-text-wrapper mb-2">
                            <p className="main-p">
                              <strong>TSP </strong>
                              <span className="sub-p">
                                {" "}
                                Current Contributions on Paycheck
                              </span>
                            </p>
                          </div>
                          <div className="text-space">
                            {formData["pay_s2_100"] == "%" ? (
                              <Row>
                                <Col md={1}></Col>
                                <Col md={2}>Roth %</Col>
                                <Col md={4}>
                                  {showValue(formData["pay_tsp_r_1"])}{" "}
                                  {formData["pay_s2_tsp_r_percent"]
                                    ? "(" +
                                      formData["pay_s2_tsp_r_percent"] +
                                      "%" +
                                      ")"
                                    : ""}
                                </Col>
                                <Col md={5}></Col>
                              </Row>
                            ) : (
                              formData["pay_s2_100"] == "$" && (
                                <Row>
                                  <Col md={1}></Col>
                                  <Col md={2}>Roth Fixed</Col>
                                  <Col md={4}>
                                    {showValue(formData["pay_tsp_r_1"])}{" "}
                                  </Col>
                                  <Col md={5}></Col>
                                </Row>
                              )
                            )}

                            {formData["pay_s2_25"] == "%" ? (
                              <Row>
                                <Col md={1}></Col>
                                <Col md={2}>Traditional %</Col>
                                <Col md={4}>
                                  {showValue(formData["TRADITIONALINSURANCE"])}{" "}
                                  {formData["pay_s2_tsp_percent"]
                                    ? "(" +
                                      formData["pay_s2_tsp_percent"] +
                                      "%" +
                                      ")"
                                    : ""}
                                </Col>
                                <Col md={5}></Col>
                              </Row>
                            ) : (
                              formData["pay_s2_25"] == "$" && (
                                <Row>
                                  <Col md={1}></Col>
                                  <Col md={2}>Traditional Fixed</Col>
                                  <Col md={4}>
                                    {showValue(
                                      formData["TRADITIONALINSURANCE"]
                                    )}{" "}
                                  </Col>
                                  <Col md={5}></Col>
                                </Row>
                              )
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                <div className="body-text-wrapper  mt-3">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>TSP </strong>
                      <span className="sub-p">Balance Fund Contributions</span>
                    </p>
                  </div>
                  <div className="text-space">
                    <Row>
                      <Col md={1}></Col>
                      <Col md={3}>Estimated Balance:</Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={3} className="col-spacing">
                        Traditional:
                      </Col>

                      <Col md={4} className="">
                        {showValue(
                          formData["screen12_balance_of_traditional_tsp"]
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={3} className="col-spacing">
                        Roth:
                      </Col>
                      <Col md={4} className="">
                        {showValue(formData["screen12_balance_of_roth_tsp"])}
                      </Col>
                    </Row>
                    {formData["screen12_fund_type"] &&
                      Array.isArray(formData["screen12_fund_type"]) &&
                      formData["screen12_fund_type"].map((type, index) => (
                        <Row>
                          <Col md={1}></Col>
                          <Col md={3}>{type}</Col>
                          <Col md={4} className="text-bold-medium">
                            {formData["screen12_fund_type_percentage"] &&
                              formData["screen12_fund_type_percentage"][index]}
                            %
                          </Col>
                        </Row>
                      ))}
                  </div>
                </div>
                {formData["outstanding_tsp_loan"] == "yes" && (
                  <div className=" body-text-wrapper  mt-3 section ">
                    <div className="p-text-wrapper mb-2">
                      <p className="main-p">
                        <strong>Outstanding TSP Loan</strong>
                      </p>
                    </div>

                    <Row>
                      <Col md={1}></Col>
                      <Col md={2}>Amount</Col>
                      <Col md={4}>
                        {showValue(formData["outstanding_tsp_loan_amount"])}
                      </Col>
                      <Col md={5}></Col>
                    </Row>

                    <Row>
                      <Col md={1}></Col>
                      <Col md={2}>Purpose</Col>
                      <Col md={4}>
                        {" "}
                        {formData["traditional_fixed_purpose"]
                          ? formData["traditional_fixed_purpose"]
                          : ""}{" "}
                      </Col>
                      <Col md={5}></Col>
                    </Row>

                    <Row>
                      <Col md={1}></Col>
                      <Col md={2}>Remaining Years to Payoff</Col>
                      <Col md={4}>
                        {" "}
                        {formData[
                          "traditional_fixed_purpose_remaining_years_to_payoff"
                        ]
                          ? convertValue(
                              formData[
                                "traditional_fixed_purpose_remaining_years_to_payoff"
                              ]
                            )
                          : ""}{" "}
                      </Col>
                      <Col md={5}></Col>
                    </Row>
                  </div>
                )}

                {formData["s13_q1"] == "yes" && (
                  <div className="section  body-text-wrapper  mt-3">
                    <div className="p-text-wrapper mb-2">
                      <p className="main-p">
                        <strong>Planned expenditures/purchases </strong>
                      </p>
                    </div>

                    <Row>
                      <Col md={1}></Col>
                      <Col md={2}>Purchase description:</Col>
                      <Col md={4}>
                        {showValue(formData["s13_purchase_description"])}
                      </Col>
                      <Col md={5}></Col>
                    </Row>

                    <Row>
                      <Col md={1}></Col>
                      <Col md={2}>Total amount needed:</Col>
                      <Col md={4}>
                        {showValue(formData["s13_total_amount_needed"])}
                      </Col>
                      <Col md={5}></Col>
                    </Row>
                  </div>
                )}

                <div className="body-text-wrapper   mt-3">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>Risk Tolerance</strong>
                    </p>
                  </div>
                  <div className="text-space">
                    {() => {
                      if (
                        formData["remainYears"] &&
                        formData["remainYears"] < 5 &&
                        formData["remainYears"] >= 0
                      ) {
                        return (
                          <Row>
                            <Col md={1}></Col>
                            <Col md={4}>Less than five years to retirement</Col>
                            <Col md={4} className="fw-bold">
                              (Pre-Retirement)
                            </Col>
                          </Row>
                        );
                      }
                    }}

                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Years until retirement:</Col>
                      <Col md={4} className="fw-bold">
                        {moment(formData["desired_retirement_date"]).diff(
                          moment(),
                          "year"
                        )}{" "}
                        Years
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>Current Risk Tolerance:</Col>
                      <Col md={4} className="fw-bold">
                        LEVEL {formData["tsp_ret_field5"]} (
                        {formData["tsp_ret_field5"] >= 6 &&
                        formData["tsp_ret_field5"] <= 10
                          ? "Aggressive"
                          : formData["tsp_ret_field5"] >= 1 &&
                            formData["tsp_ret_field5"] <= 5
                          ? "Conservative"
                          : "0"}
                        )
                      </Col>
                    </Row>
                    <Row>
                      <Col md={1}></Col>
                      <Col md={4}>
                        Based on your risk tolerance, how much of your Thrift
                        Savings balance are you willing to lose?:
                      </Col>
                      <Col md={4} className="fw-bold">
                        {showValue(formData["lossBalance"])}
                      </Col>
                    </Row>

                    {formData["family_status"] == "married" && (
                      <Row>
                        <Col md={1}></Col>
                        <Col md={4}>Current Risk Tolerance (Spouse):</Col>
                        <Col md={4} className="fw-bold">
                          LEVEL {formData["spouse_risk_tol_level"]} (
                          {formData["spouse_risk_tol_level"] >= 6 &&
                          formData["spouse_risk_tol_level"] <= 10
                            ? "Aggressive"
                            : "Conservative"}
                          )
                        </Col>
                      </Row>
                    )}

                    {formData["tsp_ret_field1"] == "yes" && (
                      <Row>
                        <Col md={1}></Col>
                        <Col md={4} className="error-text">
                          **TSP fund allocations need to be adjusted to reflect{" "}
                          <strong>Retirement Time Horizon</strong>.
                        </Col>
                      </Row>
                    )}
                  </div>
                </div>
                <div className="body-text-wrapper mt-3">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>Withdrawal Options </strong>
                    </p>
                  </div>

                  <Row>
                    <Col md={1}></Col>
                    <Col md={4}>
                      What amount from your TSP account do you want to use for
                      monthly income?
                    </Col>
                    <Col md={5} className="col-4">
                      {showValue(formData["tsp_opt_amount"])}
                    </Col>
                  </Row>

                  {formData["tsp_opt_full_or_partial_wid"] == "1" && (
                    <Row className="mt-2">
                      <Col md={1}></Col>
                      <Col md={11}>
                        Selected Full or Partial Withdrawal on the TSP
                        Withdrawal Options screen
                      </Col>
                    </Row>
                  )}

                  {formData["tsp_opt_tsp_monthly_pay"] == "1" && (
                    <Row>
                      <Col md={1}></Col>
                      <Col md={3}>
                        Selected TSP Monthly Payments on the TSP Withdrawal
                        Options screen
                      </Col>
                    </Row>
                  )}

                  {formData["tsp_opt_single_life_annuity"] == "1" && (
                    <>
                      {formData["tsp_opt_single_life_annuity_tsp"] && (
                        <>
                          <Row>
                            <Col md={1}></Col>
                            <Col md={5}>SINGLE LIFE ANNUITY</Col>
                          </Row>
                          <Row>
                            <Col md={1}></Col>
                            <Col md={5}>
                              TSP - Monthly Payment:
                              {formData["SLA TSP"]}
                              (example)
                            </Col>
                          </Row>
                          <Row>
                            <Col md={1}></Col>
                            <Col md={5}>
                              <ul>
                                <li>Cash Refund</li>
                              </ul>
                            </Col>
                          </Row>
                        </>
                      )}
                    </>
                  )}

                  {formData["tsp_opt_joint_life_annuity"] == "1" && (
                    <>
                      {formData["tsp_opt_joint_life_annuity_tsp"] && (
                        <>
                          <Row>
                            <Col md={1}></Col>
                            <Col md={5}>JOINT LIFE ANNUITY</Col>
                          </Row>
                          <Row>
                            <Col md={1}></Col>
                            <Col md={5}>
                              TSP - Monthly Payment:
                              {formData["JLA TSP"]}
                              (example)
                            </Col>
                          </Row>
                          <Row>
                            <Col md={1}></Col>
                            <Col md={5}>
                              <ul>
                                <li>100% Survivorship</li>
                                <li>Cash Refund</li>
                              </ul>
                            </Col>
                          </Row>

                          {formData[
                            "tsp_opt_joint_life_annuity_private_sector"
                          ] == "1" && (
                            <>
                              <Row>
                                <Col md={1}></Col>
                                <Col md={5}>JOINT LIFE ANNUITY</Col>
                              </Row>
                              <Row>
                                <Col md={1}></Col>
                                <Col md={5}>
                                  Private Sector - Monthly Payment:{" "}
                                  {formData["JLA PS"]} (example)
                                </Col>
                              </Row>
                              <Row>
                                <Col md={1}></Col>
                                <Col md={4}>
                                  <ul>
                                    <li>100% Survivorship</li>
                                    <li>Cash Refund</li>
                                    <li>Emergency Saving Access</li>
                                    <li>2 Year Maximizer**</li>
                                    <li>Income Bonus</li>
                                  </ul>
                                </Col>
                              </Row>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="body-text-wrapper mt-3">
                  <div className="heading-gray-wrapper">
                    <p className="heading-gray">TSP - ITEMS OF IMPORTANCE</p>
                  </div>
                  <ul className="heading-gray-ul">
                    <li>* Taxable income at retirement</li>
                    <li>
                      * Not accessible until Age 59 1/2 (or retirement) without
                      loan or penalty unless separation occurs at age 55 or
                      later.
                    </li>
                    <li>
                      * 10% + current tax rate penalty if withdrawn before 59
                      1/2 or retirement
                    </li>
                    <li>
                      * Hardship withdrawals available if negative monthly
                      income is proven
                    </li>
                    <li>
                      * Estimated TSP Monthly Income amounts displayed on
                      planner assumes current age and selection of a single life
                      annuity with level payments and no additional features.
                      Monthly amount calculated at a multiple of .0045
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          {(formData.retirement_income_for_life_owner == "No" ||
            formData.retirement_income_for_life_spouse == "No") && (
            <section className="mt-3">
              <div className="box-wrapper">
                <div className="box-body">
                  <p className="heading">Principal Protected Growth</p>
                  <div className="body-text-wrapper text-center">
                    <Image src={Images.hypothicalImage} />
                  </div>
                </div>
              </div>
            </section>
          )}
          <section className="mt-3">
            <div className="box-wrapper">
              <div className="box-body">
                <p className="heading">Retirement Income Timeline</p>
                <div className="body-text-wrapper">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>Today:</strong>
                    </p>
                  </div>
                  <div className="text-space">
                    <Row>
                      <Col md={1}></Col>
                      <Col md={3}>Avg. Monthly Income NET:</Col>
                      <Col md={3} className="">
                        {formData.monthlyGross
                          ? "$" +
                            formatValue(convertValue(formData.monthlyGross))
                          : "$0.0"}
                      </Col>
                      <Col md={3}></Col>
                    </Row>
                  </div>
                  {generateIncomesTimeline()}
                </div>
              </div>
            </div>
          </section>
          <section className="mt-3">
            <div className="box-wrapper">
              <div className="box-body">
                <p className="heading">Emergency Cash</p>

                <div className="body-text-wrapper">
                  <div className="p-text-wrapper mb-2">
                    <p className="main-p">
                      <strong>Total Available Emergency Cash</strong>
                    </p>
                  </div>
                  <div className="text-space">
                    <Row>
                      <Col md={1}></Col>
                      <Col md={5}>
                        Minimum Emergency Savings<br></br> (
                        {formData["age"] < 65 ? "6 Months" : "12 Months"}):
                      </Col>
                      <Col md={3} className="">
                        {showValue(savings)} (Estimate)
                      </Col>
                      <Col md={3}></Col>
                    </Row>
                    {formData["s14_account_type"] &&
                      Array.isArray(formData["s14_account_type"]) &&
                      formData["s14_account_type"].map((key, index) => (
                        <Row>
                          <Col md={1}></Col>
                          <Col md={5}>
                            {formData["s14_institution_name"] &&
                              formData["s14_institution_name"][index]}
                          </Col>
                          <Col md={3}>
                            {showValue(
                              formData["s14_balance"] &&
                                formData["s14_balance"][index]
                            )}{" "}
                            ({key})
                          </Col>
                          <Col md={3}></Col>
                        </Row>
                      ))}

                    <Row>
                      <Col md={1}></Col>
                      <Col md={5} className="text-bold table-border-top">
                        Total
                      </Col>
                      <Col md={3} className="text-bold table-border-top">
                        {showValue(formData["totalInstitutionalBalance"])}
                      </Col>
                    </Row>
                    {formData["emergencyCashNeeded"] > 0 ? (
                      <Row>
                        <Col md={1}></Col>
                        <Col md={5} className="total-text-red">
                          Emergency Cash Needed
                        </Col>
                        <Col md={4} className="total-text-red">
                          {showValue(formData["emergencyCashNeeded"])}{" "}
                          <span className="conclusion-text">
                            (at retirement)
                          </span>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col md={1}></Col>
                        <Col
                          md={5}
                          className="conclusion-green"
                          style={{ fontSize: "18px", fontWeight: "500" }}
                        >
                          Sufficient Emergency Cash On Hand
                        </Col>
                        <Col md={4} className="total-text-red"></Col>
                      </Row>
                    )}
                  </div>
                </div>

                <div className="body-text-wrapper mt-3">
                  <div className="heading-gray-wrapper">
                    <p className="heading-gray">
                      Emergency Cash - ITEMS OF IMPORTANCE
                    </p>
                  </div>
                  <ul className="heading-gray-ul">
                    {formData["emergencyCashNeeded"] > 0 && (
                      <li>
                        * Additional emergency cash needed (
                        {showValue(formData["emergencyCashNeeded"])})
                      </li>
                    )}
                    <li>* In addition to TSP account</li>
                    <li>
                      * Used for career emergency expenses and retirement
                      preparation
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          <section className="mt-3">
            <div className="notes-container">
              Important Notes to File (Optional)
            </div>
            <div className="notes-text">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlTextarea1"
              >
                <Form.Control
                  className="notes-textarea"
                  as="textarea"
                  name="agentNotes"
                  value={formData["agentNotes"]}
                  onChange={(event) => onChangeHandler(event, setformData)}
                  placeholder="Include any notes that will be helpful for the federal employee you're assisting. These notes will not included in their summary."
                  rows={5}
                  autoComplete="off"
                />
              </Form.Group>
            </div>
          </section>
          {validated && (
            <div className="red-text text-center mt-2">
              *Complete required fields to continue
            </div>
          )}
          <div className="mt-4 mb-2 d-flex justify-content-center submit">
            <div className="text-center">
              <button className="btn-next btn question-next" type="submit">
                CONTINUE
              </button>
            </div>
          </div>
        </Form>
      </Card.Body>
    </>
  );
};

export default ConclusionForm;
